<template>

  <v-container fluid class="user-settings-facility-agent">

    <!--------------------------->
    <!-- FA License Management -->
    <!--------------------------->
    <v-container fluid class="pt-4 pb-9 page-content" v-if="userToEdit">
      <!-- FA license title -->
      <v-row class="pa-0 ma-0">
        <v-col cols="12">
          <span class="title">Arizona Facility Agent (FA) License</span>
        </v-col>
      </v-row>

      <!-- Not configured view (first time) -->
      <template v-if="!isConfigured && !initConfigure">
        <v-row align="center" class="pa-0 ma-0 mt-4">
          <v-col cols="12">
            <v-btn @click="initConfigure = true">
              Provide FA license info
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <!-- Manage view (after initial configuration) -->
      <template v-else>
        <v-row>
          <!-- Upload / Show FA image -->
          <v-col cols="12" xs="12" sm="12" md="6" lg="6">
            <template v-if="isEditingImage">
              <asset-uploader
                  label="Upload FA License"
                  :showCancel="true"
                  :resizeImage="true"
                  assetType="fa_license"
                  assetUploadUri="my/user/asset/uploadUrl"
                  v-on:canceled="cancelUploadImage"
                  v-on:saved="handleSavedFaLicenseImage" />
            </template>
            <template v-else>
              <v-row class="pa-0 ma-0">
                <v-col class="pa-0 ma-0" align="center">
                  <v-progress-circular indeterminate :size="40" color="primary" style="margin-top:55px;" v-if="isImageLoading" />
                  <div class="file-container d-flex align-center justify-center" v-else>
                    <template v-if="imageFaLicense">
                      <embed
                          v-if="isDocType"
                          height="400"
                          width="96%"
                          :src="imageFaLicense" />
                      <v-img
                          v-else
                          contain
                          max-width="100%"
                          :max-height="300"
                          :src="imageFaLicense" />
                    </template>
                    <div style="color:#727272;" v-else>(no card uploaded)</div>
                  </div>
                </v-col>
              </v-row>
              <v-row class="pa-0 ma-0 mt-3">
                <v-col class="pa-0 ma-0 d-flex flex-column align-center justify-center" v-if="!isImageLoading">
                  <div v-if="imageFaLicense" class="mt-4">
                    <v-btn @click="enableUploadImage">
                      <span>Change file</span>
                    </v-btn>
                  </div>
                  <v-btn
                      v-else
                      class="mt-4"
                      color="primary"
                      @click="enableUploadImage">
                    <span>Upload FA License Card</span>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-col>

          <!-- License info -->
          <v-col cols="12" xs="12" sm="12" md="6" lg="6" class="pt-0">
            <!-- License number -->
            <v-row align="center" class="pa-0 ma-0">
              <v-col cols="12" xs="12" sm="12" md="4" lg="3">
                <span class="label">License #</span>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="7" lg="7">
                <v-text-field
                    outlined
                    hide-details
                    :disabled="isUserInfoSaving"
                    :rules="$root.rules.fieldRequired"
                    @input="setDirty('fa_license_number')"
                    v-model="userToEdit.fa_license_number" />
              </v-col>
            </v-row>

            <!-- Issue date -->
            <v-row align="center" class="pa-0 ma-0 mt-2">
              <v-col cols="12" xs="12" sm="12" md="4" lg="3" class="pt-2">
                <span class="label">Issue date:</span>
              </v-col>
              <v-col cols="12" xs="12" sm="12" md="7" lg="6">
                <span>{{ formatDate(userToEdit.fa_license_issue_date) }}</span>
                <v-btn
                    text
                    class="ml-3"
                    @click="openDateSelector('fa_license_issue_date')">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <!-- Expiration date -->
            <v-row align="center" class="pa-0 ma-0 mt-4">
              <v-col cols="12" xs="12" sm="12" md="4" lg="3" class="pt-2">
                <span class="label">Expiration:</span>
              </v-col>
              <v-col cols="12" xs="12" sm="12" md="7" lg="6">
                <span>{{ formatDate(userToEdit.fa_license_expiration) }}</span>
                <v-btn
                    text
                    class="ml-3"
                    @click="openDateSelector('fa_license_expiration')">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="divider my-10 ml-4 mr-5"></v-row>

        <!-- User info actions (cancel, save) -->
        <v-row class="mt-0">
          <v-col cols="6" class="pl-8 pt-0">
            <v-btn
                v-if="isDirty && !isUserInfoSaving"
                color="gray"
                width="100"
                @click="cancel">
              Cancel
            </v-btn>
          </v-col>
          <v-col cols="6" class="pr-8 pt-0" align="right">
            <v-btn
                v-if="isDirty && !isUserInfoSaving"
                color="primary"
                width="110"
                @click="saveUserInfo">
              Save
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </v-container>

    <!------------------------------------>
    <!-- Date Selector (for both dates) -->
    <!------------------------------------>
    <v-dialog persistent max-width="350" v-model="isDateSelectorOpen">
      <v-card>
        <v-card-text class="date-picker-body">
          <v-date-picker v-model="selectedDate" />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="cancelDateSelection">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
              text
              color="primary"
              @click="setSelectedDate">
            Select
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
import AssetUploader from '@/components/app/AssetUploader'

export default {
  name: 'UserSettingsFacilityAgent',

  components: {
    AssetUploader
  },

  data () {
    return {
      initConfigure: false,
      // ------------ manage user info
      isDirty: false,
      dirtyProps: [],
      isUserInfoSaving: false,
      saveErrorMessage: null,
      // ------------ manage license info
      isDateSelectorOpen: false,
      selectedDateFor: null,
      selectedDate: null,
      // ------------ manage uploaded image
      isImageLoading: false,
      loadingErrorMessage: null,
      imageFaLicense: null,
      isEditingImage: false,
      isImageType: false,
      isDocType: false
    }
  },

  computed: {
    activeUser () {
      return this.$store.getters['auth/activeUser']
    },
    userToEdit () {
      return this.$store.getters['auth/activeUserEditing']
    },
    isConfigured () {
      return (
        this.activeUser.fa_license_number &&
        this.activeUser.fa_license_state_code &&
        this.activeUser.fa_license_issue_date &&
        this.activeUser.fa_license_expiration
      ) ? true : false
    }
  },

  async beforeMount () {
    this.loadFaLicenseImage()
  },

  methods: {
    // Manage dirty state
    setDirty (propName) {
      if (this.activeUser[propName] !== this.userToEdit[propName]) {
        if (!this.$root.utils.includes(this.dirtyProps, propName)) this.dirtyProps.push(propName)
        this.isDirty = true
      } else {
        const dirtyPropIndex = this.dirtyProps.findIndex(prop => prop === propName)
        if (dirtyPropIndex !== -1) this.dirtyProps.splice(dirtyPropIndex, 1)
        if (this.dirtyProps.length === 0) this.isDirty = false
      }
    },

    // Cancel changes
    cancel () {
      this.$store.dispatch('auth/setActiveUserToEdit') // reset user info
      this.dirtyProps = []
      this.isDirty = false
    },

    // Format date for display
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MM/DD/YYYY') : ''
    },

    // Open date selector
    openDateSelector (fieldName) {
      this.selectedDateFor = fieldName
      this.isDateSelectorOpen = true
    },

    // Cancel date change action
    cancelDateSelection () {
      // Clear selections and close modal
      this.selectedDateFor = null
      this.selectedDate = null
      this.isDateSelectorOpen = false
    },

    // Set date change
    setSelectedDate () {
      const dateFor = this.selectedDateFor
      const date = this.selectedDate
      this.userToEdit[dateFor] = date

      // Flag dirty
      this.dirtyProps.push(dateFor)
      this.isDirty = true

      // Clear selections and close modal
      this.selectedDateFor = null
      this.selectedDate = null
      this.isDateSelectorOpen = false
    },

    // Enable upload of image
    enableUploadImage () {
      this.isEditingImage = true
    },

    // Cancel upload of image
    cancelUploadImage () {
      this.isEditingImage = false
    },

    // Handle saved image upload
    handleSavedFaLicenseImage () {
      this.loadFaLicenseImage()
      this.cancelUploadImage()

      // Set saved flag
      this.userToEdit.saved_fa_license = true
      this.saveUserInfo()
    },

    // Load FA license image
    async loadFaLicenseImage () {
      this.isImageLoading = true

      try {
        const faLicense = await this.$root.fetchUserAsset('fa_license')

        // Fetch the file and check its type
        const response = await fetch(faLicense)
        const contentType = response.headers.get('content-type')

        // Set the URL and log the content type
        this.imageFaLicense = faLicense
        this.isImageType = contentType.startsWith('image/')
        this.isDocType = contentType === 'application/pdf'
      } catch (err) {
        this.loadingErrorMessage = err.message || err.text || err
      } finally {
        this.isImageLoading = false
      }
    },

    // Save user info changes
    async saveUserInfo () {
      this.isUserInfoSaving = true

      try {
        // Update user
        this.userToEdit.fa_license_state_code = 'AZ' // must be AZ
        await this.$store.dispatch('auth/updateUser')

        // Clear dirty
        this.dirtyProps = []
        this.isDirty = false
      } catch (err) {
        this.saveErrorMessage = err.message || err.text || err
      } finally {
        this.isUserInfoSaving = false
      }
    }
  }
}
</script>

<style scoped>
  .user-settings-facility-agent .file-container {
    border: 1px solid #525252;
    padding: 12px 0;
    min-height: 200px;
    text-align: center;
  }
</style>
